import React, {useEffect} from 'react';
import styled from 'styled-components';
import 'driver.js/dist/driver.min.css';
import cssConstants from '../../constants/cssConstants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {Glyphs, ControlButton, TscButtonType, TscThemeName, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import {IConversationData} from '../../model/conversationModel';
import {conversationsStore} from '../../context/conversationsProvider';
import {mediaStore} from '../../context/mediaProvider';
import {ITranscript} from '../../model/captionModels';
import {transcriptStore} from '../../context/transcriptProvider';
import config from '../../service/config';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import tabNames from '../../constants/tabNames';
import {mediaTabsStore, updateMediaTabsState} from '../../context/mediaTabsProvider';
import Constants from '../../constants/Constants';
import {showWatchProductMessage} from '../../util/MarketingHelper';

const MediaTabsToggleButtonsContainer = styled.div<{hideConversationButton: boolean; isVideo: boolean}>`
   height: 2rem;
   display: ${props => props.hideConversationButton ? 'none' : 'block'};
   z-index: ${cssConstants.zIndex.conversation};
   outline: none;
   top: 1rem;
   right: 1.25rem;
   position: ${props => props.isVideo ? 'absolute' : 'static'};

   button {
      width: 4rem;
      height: 4rem;
      border-radius: ${TscStyles.border.radius.xxl};
   }

   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px) {
      top: 4rem;
      right: 1.25rem;
      position: absolute;
   }

   // hide when width is under mediaTabsDisplayBreakpoint
   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px) {
      display: none;
   }
`;

const MediaTabsToggleButtons = styled.div`
   display: flex;
   flex-direction: column;
   background-color: rgb(46 57 64 / 80%);
   backdrop-filter: blur(24px);
   box-shadow: 0px 1px 3px 0px #FFFFFF4D inset;
   border-radius: ${TscStyles.border.radius.xxl};
`;

export const MediaTabsToggleButtonBase: React.FC<IMediaTabsToggleButtonProps & IStateMappedProps> = ({hidden, mediaTabsPanelOpen, conversations, VideoReviewId, transcript, IsVideo}) => {
   const [hasConversation, setHasConversation] = React.useState(false);
   const [hasTranscript, setHasTranscript] = React.useState(false);

   useEffect(() => {
      setHasTranscript(!!transcript);
   }, [transcript]);

   useEffect(() => {
      setHasConversation(!!VideoReviewId || (config.featureSwitches.UseSingleConversation && conversations && conversations.length > 0));
   }, [VideoReviewId, conversations]);

   const onButtonClick = (newTab: string) => {
      updateMediaTabsState({currentTab: newTab, mediaTabsPanelOpen: true});

      window.setTimeout(() => {
         document.getElementById('side-panel-container-pane')?.focus({preventScroll: showWatchProductMessage()});
      }, Constants.getDomElementTimeout);
   };

   return (
      <MediaTabsToggleButtonsContainer
         tabIndex={-1}
         data-test-id="tab-panel-toggle-buttons"
         hideConversationButton={hidden || mediaTabsPanelOpen}
         isVideo={IsVideo}
      >
         <MediaTabsToggleButtons>
            {(hasConversation || config.watchConfig.isOwnerOrAdmin) && (
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  themeName={TscThemeName.dusk}
                  isFullWidth={false}
                  onClick={() => onButtonClick(tabNames.comments)}
                  title={tabNames.comments}
                  testId="toggle-comments-button"
                  glyph={<Glyphs.Comment24x24 />}
               />
            )}
            {hasTranscript && (
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  themeName={TscThemeName.dusk}
                  isFullWidth={false}
                  onClick={() => onButtonClick(tabNames.transcript)}
                  title={tabNames.transcript}
                  testId="toggle-transcript-button"
                  glyph={<Glyphs.Transcript24x24 />}
               />
            )}
            {config.watchConfig.isOwnerOrAdmin && (
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  themeName={TscThemeName.dusk}
                  isFullWidth={false}
                  onClick={() => onButtonClick(tabNames.settings)}
                  title={tabNames.settings}
                  testId="toggle-settings-button"
                  glyph={<Glyphs.EditSettings24x24 />}
               />
            )}
         </MediaTabsToggleButtons>
      </MediaTabsToggleButtonsContainer>
   );
};

export interface IMediaTabsToggleButtonProps {
   hidden?: boolean;
}

export interface IStateMappedProps {
   mediaTabsPanelOpen: boolean;
   conversations: IConversationData[];
   VideoReviewId: string;
   transcript: ITranscript;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {hidden}: IMediaTabsToggleButtonProps,
   {mediaTabsPanelOpen}: Partial<IStateMappedProps>,
   {conversations}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>,
   {transcript}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): IStateMappedProps & IMediaTabsToggleButtonProps => ({hidden, mediaTabsPanelOpen, conversations, VideoReviewId, transcript, IsVideo});

export default withMemoizedContexts(mapStatesToProps, mediaTabsStore, conversationsStore, selectedConversationStore, transcriptStore, mediaStore)(MediaTabsToggleButtonBase);
